import { Box, Divider, Stack, Typography } from '@mui/material';
import React from 'react';

// Themed card-style section.  Renders an optional icon, title +
// optional description header, a divider, and the body slot beneath.
// Matches the theme-new visual idiom used by `<ContentSection>`
// elsewhere in settings views but is local to user-settings so we
// can adjust independently.
//
// Pass `icon` as a rendered MUI icon node (e.g. <NotificationsIcon />)
// — it gets a soft tinted background bubble for visual hierarchy.
//
// `chip` renders an MUI Chip next to the title (matches the
// `headerContent` idiom used by ContentSection elsewhere in
// settings views).  Use it to flag gated sections — "Admin Only",
// "Staff Only", etc.  Pass a chip element you've already
// configured, e.g. `<Chip label="Admin Only" color="primary"
// size="small" />`.
export default function SettingsSection({ id, icon, title, description, chip, action, children, sx }) {
  return (
    <Box
      id={id}
      // Scroll anchor offset so the rail's smooth-scroll lands the
      // section title below the TitleBar instead of flush with the
      // scroll container's top edge.
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 'var(--card-radius)',
        border: 1,
        borderColor: 'divider',
        px: { xs: 2, md: 3 },
        py: 2.5,
        scrollMarginTop: 16,
        ...sx,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          {icon ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 'var(--card-icon-radius)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'action.hover',
                color: 'primary.main',
                flexShrink: 0,
                '& svg': { fontSize: 22 },
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" component="h2" sx={{ lineHeight: 1.3 }}>
                {title}
              </Typography>
              {chip}
            </Stack>
            {description ? (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Stack>
      <Divider sx={{ my: 2.5 }} />
      <Box>{children}</Box>
    </Box>
  );
}
